import { useCallback } from "react";
import { useRemoteParticipant } from "@livekit/components-react";
import { LocalParticipant, ParticipantKind } from "livekit-client";
import type { ProviderRaiseHand } from "../types";
import { ACTION_TYPES, sendSignal } from "./helpers";
import { useLivekitLocalParticipant } from "./useLivekitLocalParticipant";

export const useLivekitRaiseHand = (peerId?: string): ProviderRaiseHand => {
  const localParticipant = useLivekitLocalParticipant();
  const participant = useRemoteParticipant({
    kind: ParticipantKind.STANDARD,
    identity: peerId,
  });
  const selectedParticipant =
    !peerId || peerId === localParticipant.identity
      ? localParticipant
      : participant;

  const isHandRaised = selectedParticipant?.attributes?.isHandRaised === "true";

  const updateRaiseHand = useCallback(
    async (isHandRaised: boolean) => {
      if (selectedParticipant instanceof LocalParticipant) {
        await selectedParticipant.setAttributes({
          isHandRaised: isHandRaised.toString(),
        });
      } else if (selectedParticipant) {
        await sendSignal(
          ACTION_TYPES.LOWER_HAND,
          localParticipant,
          selectedParticipant.identity,
        );
      }
    },
    [localParticipant, selectedParticipant],
  );

  const raiseHand = useCallback(async () => {
    await updateRaiseHand(true);
  }, [updateRaiseHand]);

  const lowerHand = useCallback(async () => {
    await updateRaiseHand(false);
  }, [updateRaiseHand]);

  const toggleHand = useCallback(async () => {
    await updateRaiseHand(!isHandRaised);
  }, [updateRaiseHand, isHandRaised]);

  return {
    isHandRaised,
    raiseHand,
    lowerHand,
    toggleHand,
  };
};
